import {FeatureName} from '@contentlab/types/project';
import translate from '@/utils/translate';

interface TooltipContent {
  headline: string;
  image: string;
  items: {title: string; description: string}[];
}

export default function generateBestPracticesTooltipContent(
  selectedFeature: FeatureName
): TooltipContent {
  const featureToContentMap: Record<FeatureName, TooltipContent> = {
    [FeatureName.REFORMATTING]: {
      headline: translate('Choose the Right Videos to Resize'),
      image:
        'https://storage.googleapis.com/brands-public-assets/content-lab/Onboarding/Reframe_Tooltip.png',
      items: [
        {
          title: translate('Well-Framed Shot'),
          description: translate(
            'Clips where the subject is centered and not too close to the edges.'
          ),
        },
        {
          title: translate('Minimal Graphics'),
          description: translate(
            'Videos without overlays or text resize more cleanly.'
          ),
        },
        {
          title: translate('Stable, Bright Clips'),
          description: translate(
            'Well-lit, steady footage keeps the subject clear and easy to track.'
          ),
        },
      ],
    },
    [FeatureName.AI_DUBBING]: {
      headline: translate('Choose the Right Videos to Dub'),
      image:
        'https://storage.googleapis.com/brands-public-assets/content-lab/Onboarding/Dub_Tooltip.png',
      items: [
        {
          title: translate('Face the Camera'),
          description: translate(
            'Speakers who are front-facing and well-lit sync more accurately.'
          ),
        },
        {
          title: translate('Visible Mouth'),
          description: translate(
            'Lips in clear view improve dubbing alignment.'
          ),
        },
        {
          title: translate('Record in a Quiet Space'),
          description: translate(
            'Low background noise helps maintain clean voice matching.'
          ),
        },
      ],
    },
    [FeatureName.REPHRASING]: {
      headline: translate('Choose the Right Videos to Rescript'),
      image:
        'https://storage.googleapis.com/brands-public-assets/content-lab/Onboarding/Rephrase_Tooltip.png',
      items: [
        {
          title: translate('Pause Between Lines'),
          description: translate(
            'Clips with natural breaks between sentences for smoother edits.'
          ),
        },
        {
          title: translate('Minimize Background Noise'),
          description: translate(
            'One clear voice keeps speech clean and in sync.'
          ),
        },
      ],
    },
    [FeatureName.CAPTIONS]: {
      headline: translate('Choose the Right Videos to Caption'),
      image:
        'https://storage.googleapis.com/brands-public-assets/content-lab/captions-guidance.png',
      items: [
        {
          title: translate('Use Clear, Dialogue-Focused Audio'),
          description: translate(
            'Speech recorded in a quiet space with minimal background noise.'
          ),
        },
        {
          title: translate('One Speaker at a Time'),
          description: translate(
            'No cross-talk, so each voice is captured accurately.'
          ),
        },
        {
          title: translate('Complete Sentences'),
          description: translate(
            'Clips where the speaker finishes full thoughts for easier reading.'
          ),
        },
      ],
    },
    [FeatureName.AI_AVATAR]: {
      headline: translate('Choose the Right Videos for AI Avatar'),
      image:
        'https://storage.googleapis.com/brands-public-assets/content-lab/Onboarding/Avatar_Tooltip.png',
      items: [
        {
          title: translate('Face the Camera'),
          description: translate(
            'Make sure the speaker is well-lit and front-facing.'
          ),
        },
        {
          title: translate('Keep Face Visible'),
          description: translate(
            'Avoid hands, objects, or hair blocking the face.'
          ),
        },
        {
          title: translate('Use Stable Video'),
          description: translate(
            'Choose videos with minimal camera movement and good lighting.'
          ),
        },
      ],
    },
    [FeatureName.UPSCALE]: {
      headline: translate('Choose the Right Videos for Upscale'),
      image:
        'https://storage.googleapis.com/brands-public-assets/content-lab/Onboarding/Upscale_Tooltip.png',
      items: [
        {
          title: translate('High-Quality Source'),
          description: translate(
            'Well-lit, steady footage keeps the subject clear and easy to track.'
          ),
        },
        {
          title: translate('Low Compression and Noise'),
          description: translate(
            'Clean, uncompressed clips upscale more smoothly.'
          ),
        },
        {
          title: translate('Steady, Clear Footage'),
          description: translate('Less motion blur means sharper results.'),
        },
      ],
    },
  };

  const featureContentMap = featureToContentMap[selectedFeature];
  if (!featureContentMap) {
    throw new Error('Could not find feature content');
  }

  return featureContentMap;
}
